import { Routes, Route, Navigate } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';

// Import page components (default exports with permissions and layouts)
import LoginPage from '@/pages/login';
import UnAuthorizedPage from '@/pages/UnAuthorizedPage';
import NotFoundPage from '@/pages/404';
import { initI18nNext } from '@/i18n';

// Import all page components
import CustomersPage from '@/pages/kunden/index';
import CustomerDetailsPage from '@/pages/kunden/details';
import TicketsPage from '@/pages/tickets/index';
import TicketDetailsPage from '@/pages/tickets/details';
import VouchersPage from '@/pages/vouchers/index';
import OrderDetailsPage from '@/pages/kunden/bestellungen/index';
import LiftOperatorsPage from '@/pages/bergbahnen/index';
import FaceValidationPage from '@/pages/foto-kalibrierung/index';
import CouponsPage from '@/pages/coupons/index';
import FaqPage from '@/pages/faq/index';
import TextReplacementsPage from '@/pages/text-ersetzungen/index';
import WebshopsPage from '@/pages/webshops/index';
import AgenciesPage from '@/pages/agenturen/index';
import MailsPage from '@/pages/mails/index';
import BillingsPage from '@/pages/abrechnungen/index';
import ProductionPage from '@/pages/produktion/index';
import VoucherTemplatesPage from '@/pages/gutscheinvorlagen/index';
import VoucherTemplateEditorPage from '@/pages/gutscheinvorlagen/bearbeiten';
import ReportsPage from '@/pages/auswertungen/index';

import '@/styles/global.css';

import 'simplebar-react/dist/simplebar.min.css';
import { PageWithLayout } from '@/pages/_app';
import CashDesksPage from '@/pages/cash-desks';
import { useMemo } from 'react';

const routesConfig = {
    routes: [
        {
            path: '/login',
            component: LoginPage,
        },
        {
            path: '/unauthorized',
            component: UnAuthorizedPage,
        },
        {
            path: '/kunden',
            component: CustomersPage,
        },
        {
            path: '/kunden/:customerId',
            component: CustomerDetailsPage,
        },
        {
            path: '/tickets',
            component: TicketsPage,
        },
        {
            path: '/tickets/details',
            component: TicketDetailsPage,
        },
        {
            path: '/vouchers',
            component: VouchersPage,
        },
        {
            path: '/kunden/:customerId/bestellungen/:orderId',
            component: OrderDetailsPage,
        },

        {
            path: '/bergbahnen',
            component: LiftOperatorsPage,
        },
        {
            path: '/foto-kalibrierung',
            component: FaceValidationPage,
        },
        {
            path: '/auswertungen',
            component: ReportsPage,
        },
        {
            path: '/coupons',
            component: CouponsPage,
        },
        {
            path: '/faq',
            component: FaqPage,
        },
        {
            path: '/text-ersetzungen',
            component: TextReplacementsPage,
        },
        {
            path: '/webshops',
            component: WebshopsPage,
        },
        {
            path: '/agenturen',
            component: AgenciesPage,
        },
        {
            path: '/cash-desks',
            component: CashDesksPage,
        },
        {
            path: '/mails',
            component: MailsPage,
        },
        {
            path: '/abrechnungen',
            component: BillingsPage,
        },
        {
            path: '/produktion',
            component: ProductionPage,
        },
        {
            path: '/gutscheinvorlagen',
            component: VoucherTemplatesPage,
        },
        {
            path: '/gutscheinvorlagen/bearbeiten',
            component: VoucherTemplateEditorPage,
        },
    ],
    redirects: [
        {
            from: '/',
            to: '/kunden',
        },
    ],
    fallback: {
        path: '*',
        component: NotFoundPage,
    },
};

function App() {
    const renderRoute = (
        route: { path: string; component: PageWithLayout },
        key: string
    ) => {
        const Component = route.component;

        // Use the page component's built-in layout
        const layoutComponent = Component.getLayout;

        return (
            <Route
                key={key}
                path={route.path}
                element={
                    layoutComponent ? (
                        layoutComponent(<Component />)
                    ) : (
                        <Component />
                    )
                }
            />
        );
    };

    const i18n = useMemo(() => initI18nNext(), []);

    return (
        <I18nextProvider i18n={i18n}>
            <Routes>
                {/* Redirects */}
                {routesConfig.redirects.map((redirect, index) => (
                    <Route
                        key={`redirect-${index}`}
                        path={redirect.from}
                        element={<Navigate to={redirect.to} replace />}
                    />
                ))}

                {/* All routes */}
                {routesConfig.routes.map((route, index) =>
                    renderRoute(route, `route-${index}`)
                )}

                {/* Fallback route */}
                {renderRoute(routesConfig.fallback, 'fallback')}
            </Routes>
        </I18nextProvider>
    );
}

export default App;
